import * as React from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { FormSelectField, Grid, Typography } from 'bento';
import { dashboard } from 'types';
import { durationOptions } from 'components/inputs/SelectDuration';
import _ from 'lodash';
import pluralize from 'pluralize';

export interface BasicTurnTimeFields {
  turnTimes: TurnTimeField[];
}

type TurnTimeField = {
  turnTimeMinutes: string;
  partySize: number;
};

interface Props {
  isDisabled?: boolean;
  name?: string;
}

export const getDefaultTurnTimeFields = (): TurnTimeField[] => [
  { partySize: 1, turnTimeMinutes: '90' },
  { partySize: 2, turnTimeMinutes: '90' },
  { partySize: 3, turnTimeMinutes: '105' },
  { partySize: 4, turnTimeMinutes: '105' },
  { partySize: 5, turnTimeMinutes: '120' },
  { partySize: 6, turnTimeMinutes: '120' },
  { partySize: 7, turnTimeMinutes: '135' },
  { partySize: 8, turnTimeMinutes: '135' },
  { partySize: 9, turnTimeMinutes: '150' },
  { partySize: 10, turnTimeMinutes: '150' },
  { partySize: 11, turnTimeMinutes: '150' },
  { partySize: 12, turnTimeMinutes: '150' },
];

/**
 * Transforms schedule turn time data in to form field objects
 */
export const encodeTurnTimeFields = (basicTurnTimes: dashboard.ISingleTurnTime[]) =>
  _.compact(
    basicTurnTimes.map(({ partySize, turnTimeMinutes }) =>
      partySize && turnTimeMinutes
        ? {
            turnTimeMinutes: String(turnTimeMinutes),
            partySize,
          }
        : undefined
    )
  );

/**
 * Transforms form field objects in to schedule turn times
 */
export const decodeTurnTimeFields = (turnTimeFields: TurnTimeField[]): dashboard.SingleTurnTime[] =>
  turnTimeFields.map(({ turnTimeMinutes, partySize }) => ({
    turnTimeMinutes: Number(turnTimeMinutes),
    partySize,
  }));

/**
 * Formik fieldset to display turn time options by party size
 */
const TurnTimeFieldset: React.FC<React.PropsWithChildren<Props>> = ({
  name = 'turnTimes',
  isDisabled,
}) => {
  const context = useFormikContext<BasicTurnTimeFields>();
  const data = context.getFieldMeta<TurnTimeField[]>(name); // may convert this to use field

  return (
    <FieldArray
      name={name}
      render={() =>
        data.value.map(({ partySize }, idx, arr) => (
          <React.Fragment key={partySize}>
            <Grid item xs={3} container alignItems="center">
              <Typography id={`party-size-${partySize}`} variant="body">
                {partySize}
                {idx === arr.length - 1 ? `+ ` : ` `}
                {pluralize('Guest', partySize)}
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <FormSelectField
                disabled={isDisabled}
                fullWidth
                name={`${name}.${idx}.turnTimeMinutes`}
                SelectProps={{
                  inputProps: {
                    'data-testid': `turn-time-select_.${idx}`,
                    'aria-labelledby': `party-size-${partySize}`,
                  },
                }}
              >
                {durationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FormSelectField>
            </Grid>
          </React.Fragment>
        ))
      }
    />
  );
};

export default TurnTimeFieldset;
